import { assertNever } from '../../../lib/exhaustive';
import { RCU_GROUP } from '../../../lib/rcu/catalog/protocol';
import { keyNumberOfIndex } from './layout';
import { KEYPAD_ISSUE, blocking } from './issue';
import { KEYPAD_LIMITS } from './limits';
import { fail, ok } from '../result';
import type { KeypadResult } from '../result';

/**
 * Panel families: what kind of device answers, and at which address.
 *
 * The rules are the reference app's (rcu_config `services/input_address.go` and
 * `models.ReservedKeyMap`), and they matter because a family's address group is
 * what the firmware matches a key press against:
 *
 * - a standard keypad or logic board takes a technician-chosen address out of
 *   group 3, the same group ordinary RCU relay traffic uses;
 * - a corridor panel's four keys are fixed: three service keys in group 2 at
 *   address 2, and the bell in group 1 at address 1;
 * - a keycard reader is fixed at group 4, address 1.
 */
export const KEYPAD_FAMILY = {
  STANDARD: 'standardKeypad',
  CORRIDOR: 'corridorPanel',
  KEYCARD: 'keycardReader',
  LOGIC_BOARD: 'logicBoard',
} as const;

export type KeypadFamilyKey = (typeof KEYPAD_FAMILY)[keyof typeof KEYPAD_FAMILY];

export const KEYPAD_ADDRESS_MODE = {
  /** The technician picks an address in the family's group. */
  SELECTABLE: 'selectable',
  /** The family's keys carry their own fixed selectors. */
  FIXED: 'fixed',
} as const;

export type KeypadAddressMode = (typeof KEYPAD_ADDRESS_MODE)[keyof typeof KEYPAD_ADDRESS_MODE];

/** Where one key's press is announced on the wire. */
export interface KeypadSelector {
  readonly group: number;
  /** The panel (or fixed key group) address. */
  readonly address: number;
  /** The key's number on the wire, counted from one as the face is engraved. */
  readonly button: number;
}

interface FamilySpecBase {
  readonly label: string;
  /**
   * True when the family may be placed once per RCU: its keys answer on fixed,
   * shared addresses, so a second panel of the same family could not be told
   * apart from the first.
   */
  readonly singleInstance: boolean;
  readonly caveat: string | null;
}

export interface SelectableFamilySpec extends FamilySpecBase {
  readonly addressMode: typeof KEYPAD_ADDRESS_MODE.SELECTABLE;
  /** The address pool this family draws on. */
  readonly group: number;
}

export interface FixedFamilySpec extends FamilySpecBase {
  readonly addressMode: typeof KEYPAD_ADDRESS_MODE.FIXED;
  /** The selectors its keys use, in key order. */
  readonly fixedSelectors: readonly Omit<KeypadSelector, 'button'>[];
}

export type KeypadFamilySpec = SelectableFamilySpec | FixedFamilySpec;

/** Groups and addresses the fixed service keys live at, from the reference. */
export const KEYPAD_FIXED_SELECTOR = {
  CORRIDOR_SERVICE: { group: 2, address: 2 },
  CORRIDOR_BELL: { group: 1, address: 1 },
  KEYCARD: { group: 4, address: 1 },
} as const satisfies Record<string, Omit<KeypadSelector, 'button'>>;

export const KEYPAD_FAMILY_SPECS = {
  [KEYPAD_FAMILY.STANDARD]: {
    label: 'Standard keypad',
    addressMode: KEYPAD_ADDRESS_MODE.SELECTABLE,
    group: RCU_GROUP.DEFAULT,
    singleInstance: false,
    caveat: null,
  },
  [KEYPAD_FAMILY.LOGIC_BOARD]: {
    label: 'Logic board',
    addressMode: KEYPAD_ADDRESS_MODE.SELECTABLE,
    group: RCU_GROUP.DEFAULT,
    singleInstance: false,
    caveat: null,
  },
  [KEYPAD_FAMILY.CORRIDOR]: {
    label: 'Corridor panel',
    addressMode: KEYPAD_ADDRESS_MODE.FIXED,
    fixedSelectors: [
      KEYPAD_FIXED_SELECTOR.CORRIDOR_SERVICE,
      KEYPAD_FIXED_SELECTOR.CORRIDOR_SERVICE,
      KEYPAD_FIXED_SELECTOR.CORRIDOR_SERVICE,
      KEYPAD_FIXED_SELECTOR.CORRIDOR_BELL,
    ],
    singleInstance: true,
    caveat: null,
  },
  [KEYPAD_FAMILY.KEYCARD]: {
    label: 'Keycard reader',
    addressMode: KEYPAD_ADDRESS_MODE.FIXED,
    fixedSelectors: [KEYPAD_FIXED_SELECTOR.KEYCARD],
    singleInstance: true,
    caveat:
      'This firmware build does not report keycard taps — its card-pin monitor is not started — so a mapping can be written but not tried on the bench.',
  },
} as const satisfies Record<KeypadFamilyKey, KeypadFamilySpec>;

export const KEYPAD_FAMILIES = [
  KEYPAD_FAMILY.STANDARD,
  KEYPAD_FAMILY.CORRIDOR,
  KEYPAD_FAMILY.KEYCARD,
  KEYPAD_FAMILY.LOGIC_BOARD,
] as const satisfies readonly KeypadFamilyKey[];

/** True when the family may only be placed once per RCU. */
export const isSingleInstanceFamily = (family: KeypadFamilyKey): boolean =>
  KEYPAD_FAMILY_SPECS[family].singleInstance;

/** True when the technician chooses this family's address on the panel face. */
export const hasSelectableAddress = (family: KeypadFamilyKey): boolean =>
  KEYPAD_FAMILY_SPECS[family].addressMode === KEYPAD_ADDRESS_MODE.SELECTABLE;

/** The address pool a family draws on, or null when its keys are fixed. */
export const addressGroupOf = (family: KeypadFamilyKey): number | null => {
  const spec = KEYPAD_FAMILY_SPECS[family];
  return spec.addressMode === KEYPAD_ADDRESS_MODE.SELECTABLE ? spec.group : null;
};

/**
 * Where one key of one panel announces itself.
 *
 * This is the feature's whole idea of a trigger: group, address and button are
 * derived here and nowhere else, so a panel's address and its keys can never
 * disagree.
 */
export const keySelector = (
  family: KeypadFamilyKey,
  address: number | null,
  keyIndex: number,
): KeypadResult<KeypadSelector> => {
  const spec = KEYPAD_FAMILY_SPECS[family];
  switch (spec.addressMode) {
    case KEYPAD_ADDRESS_MODE.SELECTABLE: {
      if (address === null) {
        return fail(
          blocking(KEYPAD_ISSUE.ADDRESS_NOT_SET, `${spec.label} has no address yet.`),
        );
      }
      return ok({ group: spec.group, address, button: keyNumberOfIndex(keyIndex) });
    }
    case KEYPAD_ADDRESS_MODE.FIXED: {
      const fixed = spec.fixedSelectors[keyIndex];
      if (fixed === undefined) {
        return fail(
          blocking(
            KEYPAD_ISSUE.KEY_NOT_FOUND,
            `${spec.label} has ${spec.fixedSelectors.length} key(s).`,
          ),
        );
      }
      return ok({ ...fixed, button: keyNumberOfIndex(keyIndex) });
    }
    default:
      return assertNever(spec);
  }
};

/** True when the address is inside the range a technician may type. */
export const isAddressInRange = (address: number): boolean =>
  address >= KEYPAD_LIMITS.ADDRESS_MIN && address <= KEYPAD_LIMITS.ADDRESS_MAX;
